'use client';

import { useEffect } from 'react';

import { authClient } from '@/lib/better-auth-client-actions';
import {
  localPushSubscriptionForOwnership,
  releaseLocalPushSubscription,
} from '@/lib/notifications/web-push-client';

/** A sign-in that is still finishing (redirects, cookies) gets this long. */
export const SIGNED_OUT_GRACE_MS = 5_000;

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

async function hasNoSession(): Promise<boolean> {
  const { data, error } = await authClient.getSession({
    query: { disableCookieCache: true },
  });
  return !error && !data;
}

/**
 * Releases only if the browser is still signed out after the grace period,
 * so a subscription isn't dropped in the middle of a sign-in (the sign-in
 * check then keeps or adopts it).
 */
export async function releasePushIfSignedOut(
  graceMs = SIGNED_OUT_GRACE_MS
): Promise<boolean> {
  if (!(await localPushSubscriptionForOwnership())) return false;
  if (!(await hasNoSession())) return false;
  await wait(graceMs);
  if (!(await hasNoSession())) return false;
  return releaseLocalPushSubscription();
}

/**
 * Once per page load, on any page: a browser that still holds a push
 * subscription but has no session (it ended without the sign-out page:
 * expired, revoked, cookies cleared) drops the subscription, so no earlier
 * account's notifications keep arriving. Browsers without a subscription
 * make no request. Only a definite "no session", twice across a short
 * grace period, releases it; a failed check leaves it alone.
 */
export function useReleasePushWhenSignedOut() {
  useEffect(() => {
    void releasePushIfSignedOut().catch(() => false);
  }, []);
}

/** Mounted once in the root layout. */
export function SignedOutPushRelease() {
  useReleasePushWhenSignedOut();
  return null;
}
